import { FC, ChangeEvent } from 'react';
import Field from 'components/shared/form/presentational/Field';
import Form from 'components/shared/form/presentational/Form';
import TextInput from 'components/shared/form/presentational/TextInput';
import useTranslate from 'lib/languages/useTranslate';
import PostCodeAutoComplete from 'components/shared/form/presentational/PostCodeAutoComplete';
import { useDispatch } from 'react-redux';
import { removeFieldError } from 'lib/actions';

interface EditAddressDetailsProps {
    buildingNo?: string;
    buildingName?: string;
    streetAddress?: string;
    town?: string;
    postcode?: string;
    country?: string;
    handleChange: (field: string, value: string) => void;
    handleSubmit: (values: any) => void;
    isPosting: boolean;
    error: any;
    resetData: () => void;
}

const EditAddressDetails: FC<EditAddressDetailsProps> = ({
    handleChange,
    handleSubmit,
    isPosting,
    error,
    resetData,
    ...props
}) => {
    const translate = useTranslate('personalDetails.form');
    const {
        buildingNo = '',
        buildingName = '',
        streetAddress = '',
        town = '',
        postcode = '',
        country = '',
    } = props;

    const isSubmitDisabled = !streetAddress || !town || !postcode || !country;
    const dispatch = useDispatch();

    const handleCustomChange = (field: string, value: string) => {
        if (value.length > 0) {
            dispatch(
                removeFieldError(
                    field === 'buildingName' ? 'buildingNo' : 'buildingName',
                ),
            );
        }
        handleChange(field, value);
    };

    const handleFormSubmit = () => {
        handleSubmit({
            buildingNo,
            buildingName,
            streetAddress,
            town,
            postcode,
            country,
        });
    };

    return (
        <Form
            submitText={translate('save')}
            onSubmit={handleFormSubmit}
            isPosting={isPosting}
            error={error ? translate('errorMessage') : ''}
            tracking
            disabledButton={isSubmitDisabled}
        >
            <Field required>
                <PostCodeAutoComplete
                    value={postcode}
                    onChange={handleChange}
                    classes="generic-input bg mb"
                    resetData={resetData}
                />
            </Field>
            <Field name={translate('buildingNo')}>
                <TextInput
                    name="buildingNo"
                    placeholder={translate('buildingNo')}
                    value={buildingNo}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleCustomChange('buildingNo', e.target.value)
                    }
                    classes="generic-input bg"
                    hideError
                />
            </Field>
            <Field name={translate('buildingName')}>
                <TextInput
                    name="buildingName"
                    placeholder={translate('buildingName')}
                    value={buildingName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleCustomChange('buildingName', e.target.value)
                    }
                    classes="generic-input bg"
                />
            </Field>
            <Field name={translate('streetAddress')} required>
                <TextInput
                    name="streetAddress"
                    placeholder={translate('streetAddress')}
                    value={streetAddress}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleChange('streetAddress', e.target.value)
                    }
                    classes="generic-input bg"
                    required
                />
            </Field>
            <Field name={translate('townCity')} required>
                <TextInput
                    name="town"
                    placeholder={translate('townCity')}
                    value={town}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleChange('town', e.target.value)
                    }
                    required
                    classes="generic-input bg"
                />
            </Field>
            <Field name={translate('country')}>
                <TextInput
                    name="country"
                    placeholder={translate('country')}
                    value={country}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleChange('country', e.target.value)
                    }
                    required
                    classes="generic-input bg"
                    disabled
                />
            </Field>
            <Field name={translate('postcode')} required>
                <TextInput
                    name="postcode"
                    placeholder={translate('postcode')}
                    value={postcode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        handleChange('postcode', e.target.value)
                    }
                    required
                    classes="generic-input bg"
                />
            </Field>
        </Form>
    );
};

export default EditAddressDetails;
